import { PROJECTED_INVESTMENT_YEARS_COUNT } from './constants'
import { InvestmentProjection } from './types'

export enum InvestmentFrequency {
  Daily = 'daily',
  Monthly = 'monthly',
  Weekly = 'weekly',
}
const DEFAULT_INVESTMENT_FREQUENCY = InvestmentFrequency.Weekly
export const ANNUAL_RECURRING_INVESTMENT_COUNT = {
  [InvestmentFrequency.Daily]: 365.0,
  [InvestmentFrequency.Weekly]: 52.0,
  [InvestmentFrequency.Monthly]: 12.0,
}

type GenerateYearlyInvestmentProjectionInput = {
  startingAmount: number
  investmentAmount: number
  recurringInvestmentFrequency: InvestmentFrequency
}

const ANNUAL_RATE = 0.08

type CalcTotalReturnTypes = {
  investFrequency: number
  investmentAmount: number
  numOfYear: number
  startingAmount: number
}

const calcReturnFromStartingInvestment = ({
  numOfYear,
  startingAmount,
}: Omit<
  CalcTotalReturnTypes,
  'investFrequency' | 'investmentAmount'
>): number => {
  return startingAmount * Math.pow(1 + ANNUAL_RATE, numOfYear)
}

const calcReturnFromRecurringInvestment = ({
  numOfYear,
  investFrequency,
  investmentAmount,
}: Omit<CalcTotalReturnTypes, 'startingAmount'>): number => {
  return (
    (investmentAmount * (Math.pow(1.0 + ANNUAL_RATE, numOfYear) - 1.0)) /
    (Math.pow(1.0 + ANNUAL_RATE, 1.0 / investFrequency) - 1.0)
  )
}

export const calcTotalReturn = ({
  investFrequency,
  investmentAmount,
  numOfYear,
  startingAmount,
}: CalcTotalReturnTypes): number => {
  return (
    calcReturnFromStartingInvestment({ numOfYear, startingAmount }) +
    calcReturnFromRecurringInvestment({
      numOfYear,
      investFrequency,
      investmentAmount,
    })
  )
}

export const generateYearlyInvestmentProjection = ({
  startingAmount,
  investmentAmount,
  recurringInvestmentFrequency,
}: GenerateYearlyInvestmentProjectionInput): InvestmentProjection[] => {
  const calcTotalInvested = (
    numOfYear: number,
    investFrequency: number,
  ): number => investmentAmount * numOfYear * investFrequency + startingAmount

  const frequency: InvestmentFrequency =
    recurringInvestmentFrequency || DEFAULT_INVESTMENT_FREQUENCY
  const yearlyInvestmentProjections: InvestmentProjection[] = []

  for (let year = 0; year < PROJECTED_INVESTMENT_YEARS_COUNT + 1; year++) {
    const investFrequency = ANNUAL_RECURRING_INVESTMENT_COUNT[frequency]

    yearlyInvestmentProjections.push({
      investedTotal: calcTotalInvested(year, investFrequency),
      projectionTotal: calcTotalReturn({
        investFrequency,
        investmentAmount,
        numOfYear: year,
        startingAmount,
      }),
    } as InvestmentProjection)
  }

  return yearlyInvestmentProjections
}
